import { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, Stack, Typography, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { TimekeeperService } from 'services/TimekeeperService';
import { TipoAcumulacionEnum } from 'repository/timekeeper/@types/TypeAccrualEnum';
import { AccrualCode } from 'repository/timekeeper/@types/AccrualCode';
import { Paycode } from 'repository/timekeeper/@types/PaycodeModel';
import { BalanceDominoCascadeItem } from 'repository/timekeeper/@types/BalanceDomino';

interface CascadeOrderListProps {
  typeAccrual: TipoAcumulacionEnum;
  value: BalanceDominoCascadeItem[];
  onChange: (items: BalanceDominoCascadeItem[]) => void;
}

const timekeeperService = new TimekeeperService();

export const CascadeOrderList: FC<CascadeOrderListProps> = ({ typeAccrual, value, onChange }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [accrualCodes, setAccrualCodes] = useState<AccrualCode[]>([]);
  const [paycodes, setPaycodes] = useState<Paycode[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    Promise.all([
      timekeeperService.accrual_code.getAll(),
      timekeeperService.paycodes.getPaycodes(),
    ])
      .then(([accruals, paycodes]) => {
        if (!mounted) return;
        setAccrualCodes(accruals);
        setPaycodes(paycodes);
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(t('general.alerts.fetch_error') || 'Error al cargar datos', { variant: 'error' });
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [enqueueSnackbar, t]);

  const filteredAccrualCodes = useMemo(
    () => accrualCodes.filter((ac) => ac.type_accrual === typeAccrual),
    [accrualCodes, typeAccrual]
  );

  const handleAdd = () => {
    const next = [
      ...value,
      {
        order_cascade: value.length + 1,
        deduce_accrual_code_id: null,
        move_paycode_id: null,
        transfer_accrual_code_id: null,
      } as BalanceDominoCascadeItem,
    ];
    onChange(next);
  };

  const handleRemove = (index: number) => {
    const next = value.filter((_, i) => i !== index).map((item, i) => ({ ...item, order_cascade: i + 1 }));
    onChange(next);
  };

  const moveUp = (index: number) => {
    if (index <= 0) return;
    const next = [...value];
    [next[index - 1], next[index]] = [next[index], next[index - 1]];
    const reindexed = next.map((item, i) => ({ ...item, order_cascade: i + 1 }));
    onChange(reindexed);
  };

  const moveDown = (index: number) => {
    if (index >= value.length - 1) return;
    const next = [...value];
    [next[index + 1], next[index]] = [next[index], next[index + 1]];
    const reindexed = next.map((item, i) => ({ ...item, order_cascade: i + 1 }));
    onChange(reindexed);
  };

  const updateItem = (index: number, patch: Partial<BalanceDominoCascadeItem>) => {
    const next = value.map((item, i) => (i === index ? { ...item, ...patch, order_cascade: i + 1 } : { ...item, order_cascade: i + 1 }));
    onChange(next);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle1">
          {t('accruals.balance_domino.dialog.inputs.cascade_order_title') || 'Orden en cascada'}
        </Typography>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAdd} disabled={loading}>
          {t('general.buttons.add') || 'Agregar'}
        </Button>
      </Stack>

      <Stack spacing={2}>
        {value.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {t('accruals.balance_domino.dialog.inputs.cascade_order_empty') || 'Sin elementos. Agrega una regla.'}
          </Typography>
        )}

        {value.map((item, idx) => (
          <Stack key={idx} direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton aria-label="move up" onClick={() => moveUp(idx)} disabled={idx === 0 || loading}>
                <ArrowUpwardIcon />
              </IconButton>
              <IconButton aria-label="move down" onClick={() => moveDown(idx)} disabled={idx === value.length - 1 || loading}>
                <ArrowDownwardIcon />
              </IconButton>
            </Box>
            <FormControl fullWidth>
              <InputLabel>{t('accruals.balance_domino.dialog.inputs.deduct_from') || 'Deducir de'}</InputLabel>
              <Select
                value={item.deduce_accrual_code_id ?? ''}
                label={t('accruals.balance_domino.dialog.inputs.deduct_from') || 'Deducir de'}
                onChange={(e) => updateItem(idx, { deduce_accrual_code_id: Number(e.target.value) || null })}
              >
                {filteredAccrualCodes.map((ac) => (
                  <MenuItem key={ac.accrual_code_id} value={ac.accrual_code_id}>
                    {ac.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('accruals.balance_domino.dialog.inputs.shift_paycode') || 'Desplazar código de pago'}</InputLabel>
              <Select
                value={item.move_paycode_id ?? ''}
                label={t('accruals.balance_domino.dialog.inputs.shift_paycode') || 'Desplazar código de pago'}
                onChange={(e) => updateItem(idx, { move_paycode_id: Number(e.target.value) || null })}
              >
                <MenuItem value="">
                  {t('general.select.none') || 'Ninguno'}
                </MenuItem>
                {paycodes.map((pc) => (
                  <MenuItem key={pc.paycode_id} value={pc.paycode_id}>
                    {pc.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>{t('accruals.balance_domino.dialog.inputs.transfer_to') || 'Transferir a código de pago'}</InputLabel>
              <Select
                value={item.transfer_accrual_code_id ?? ''}
                label={t('accruals.balance_domino.dialog.inputs.transfer_to') || 'Transferir a código de pago'}
                onChange={(e) => updateItem(idx, { transfer_accrual_code_id: Number(e.target.value) || null })}
              >
                <MenuItem value="">
                  {t('general.select.none') || 'Ninguno'}
                </MenuItem>
                {filteredAccrualCodes.map((ac) => (
                  <MenuItem key={ac.accrual_code_id} value={ac.accrual_code_id}>
                    {ac.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <IconButton aria-label="delete" color="error" onClick={() => handleRemove(idx)}>
              <DeleteIcon />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};
